import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  ArrowLeft,
  LogOut,
  Trash2,
  Eye,
  BarChart3,
  MessageSquare,
  FileText,
  Users,
  Activity,
  CheckCircle,
  XCircle,
  Loader2,
  Target,
  Download
} from 'lucide-react';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  institution: string;
  is_admin: boolean;
  is_active: boolean;
  approved?: boolean;
  created_at: string;
  study_group?: string;
  confirmation1?: number | string;
  confirmation2?: number | string;
  intervention1?: number | string;
  intervention2?: number | string;
  has_completed_survey?: boolean;
  survey_data?: {
    age_group?: string;
    gender?: string;
    race?: string;
    current_occupation?: string;
    area_of_expertise?: string;
    years_of_experience?: string;
    location_of_practice?: string;
  };
}

interface Session {
  id: number;
  session_id: string;
  user_id: number;
  username?: string;
  patient_id: string;
  patient_name: string;
  message_count: number;
  model_name: string;
  is_intervention: boolean;
  is_finished: boolean;
  finished_at?: string | null;
  intervention_success?: boolean | null;
  intervention_final_state?: string | null;
  has_findings?: boolean;
  created_at: string;
  updated_at: string;
}

type ResettableField = 'survey' | 'confirmation1' | 'confirmation2' | 'intervention1' | 'intervention2';

interface SessionDetails {
  session_id: string;
  patient_id: string;
  patient_name: string;
  model_name: string;
  is_intervention?: boolean;
  is_finished?: boolean;
  finished_at?: string | null;
  intervention_success?: boolean | null;
  intervention_final_state?: string | null;
  hidden_concerns?: string | null;
  created_at: string;
  updated_at: string;
  user: {
    username: string;
    email: string;
    role: string;
    institution: string;
  };
  conversation_history: Array<{
    role: string;
    content: string;
    timestamp: string;
    evaluator_analysis?: string;
  }>;
}

interface Stats {
  total_users: number;
  active_users: number;
  total_sessions: number;
  patient_stats: Array<{
    patient_id: string;
    patient_name: string;
    session_count: number;
  }>;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [currentTab, setCurrentTab] = useState('overview');
  const [users, setUsers] = useState<User[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionView, setSessionView] = useState<'all' | 'confirmation' | 'intervention'>('all');
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [viewSessionDialog, setViewSessionDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState<SessionDetails | null>(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'user' | 'session'; id: number | string } | null>(null);
  
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [batchUserDialog, setBatchUserDialog] = useState(false);
  const [newUser, setNewUser] = useState({ 
    username: '', password: '', role: '', institution: '', study_group: '', 
    confirmation1: '', confirmation2: '', intervention1: '', intervention2: ''
  });
  const [editingUser, setEditingUser] = useState<any>(null);
  const [batchData, setBatchData] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/patients');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [usersRes, sessionsRes, statsRes] = await Promise.all([
        apiClient.get('/api/admin/users'),
        apiClient.get('/api/admin/sessions', { params: { limit: 5000 } }),
        apiClient.get('/api/admin/stats')
      ]);

      setUsers(usersRes.data.users);
      setSessions(sessionsRes.data.sessions);
      setStats(statsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load admin data');
      console.error('Admin data load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSession = async (sessionId: string) => {
    try {
      const response = await apiClient.get(`/api/admin/sessions/${sessionId}/details`);
      setSelectedSession(response.data);
      setViewSessionDialog(true);
    } catch (err) {
      console.error('Failed to load session details:', err);
      alert('Failed to load session details');
    }
  };

  const handleToggleUserActive = async (userId: number) => {
    try {
      await apiClient.patch(`/api/admin/users/${userId}/toggle-active`);
      loadData();
    } catch (err) {
      alert('Failed to update user status');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      if (deleteTarget.type === 'user') {
        await apiClient.delete(`/api/admin/users/${deleteTarget.id}`);
      } else {
        await apiClient.delete(`/api/admin/sessions/${deleteTarget.id}`);
      }

      setDeleteConfirmDialog(false);
      setDeleteTarget(null);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  const openDeleteDialog = (type: 'user' | 'session', id: number | string) => {
    setDeleteTarget({ type, id });
    setDeleteConfirmDialog(true);
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const payload: any = { ...newUser };
      // Convert empty strings to null/undefined for optional fields
      ['confirmation1', 'confirmation2', 'intervention1', 'intervention2'].forEach(k => {
        if (payload[k] === '') delete payload[k];
        else payload[k] = parseInt(payload[k], 10);
      });
      if (payload.study_group === '') delete payload.study_group;

      await apiClient.post('/api/admin/users', payload);
      setAddUserDialog(false);
      setNewUser({ 
        username: '', password: '', role: '', institution: '', study_group: '', 
        confirmation1: '', confirmation2: '', intervention1: '', intervention2: ''
      });
      loadData();
      alert('User created successfully');
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setActionLoading(true);
    try {
      const payload: any = { ...editingUser };
      delete payload.id;

      delete payload.created_at;
      delete payload.is_admin; // Not editing admin status here
      
      // Convert strings to int/null
      ['confirmation1', 'confirmation2', 'intervention1', 'intervention2'].forEach(k => {
        if (payload[k] === '' || payload[k] === null) payload[k] = null;
        else payload[k] = parseInt(payload[k], 10);
      });
      if (payload.study_group === '') payload.study_group = null;

      await apiClient.patch(`/api/admin/users/${editingUser.id}`, payload);
      setEditUserDialog(false);
      setEditingUser(null);
      loadData();
      alert('User updated successfully');
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBatchUpload = async () => {
    setActionLoading(true);
    try {
      const splitDelimitedLine = (line: string): string[] => {
        const tabCount = (line.match(/\t/g) || []).length;
        const commaCount = (line.match(/,/g) || []).length;
        const delimiter = tabCount > commaCount ? '\t' : ',';
        const parts: string[] = [];
        let current = '';
        let inQuotes = false;

        for (let idx = 0; idx < line.length; idx += 1) {
          const ch = line[idx];
          if (ch === '"') {
            if (inQuotes && line[idx + 1] === '"') {
              current += '"';
              idx += 1;
            } else {
              inQuotes = !inQuotes;
            }
            continue;
          }

          if (ch === delimiter && !inQuotes) {
            parts.push(current.trim());
            current = '';
            continue;
          }

          current += ch;
        }

        parts.push(current.trim());
        return parts;
      };

      const lines = batchData
        .split(/\r?\n/)
        .map((line) => line.replace(/\r/g, ''))
        .filter((line) => line.trim().length > 0);

      if (lines.length === 0) {
        alert('No valid users found.');
        return;
      }

      const firstColumns = splitDelimitedLine(lines[0]).map((part) => part.trim().toLowerCase());
      const hasHeader = firstColumns.length >= 4 && firstColumns[0] === 'username' && firstColumns[1] === 'password';
      const dataLines = hasHeader ? lines.slice(1) : lines;

      const users = dataLines.map((line) => {
        const parts = splitDelimitedLine(line);

        if (hasHeader) {
          const [username, password, role, group, confirmation1, confirmation2, intervention1, intervention2] = parts;
          const userObj: any = { username, password, role: role || 'Participant' };
          if (group) userObj.group = group;
          if (confirmation1) userObj.confirmation1 = confirmation1;
          if (confirmation2) userObj.confirmation2 = confirmation2;
          if (intervention1) userObj.intervention1 = intervention1;
          if (intervention2) userObj.intervention2 = intervention2;
          return userObj;
        }

        const [username, password, role, group, c1, c2, i1, i2] = parts;
        const userObj: any = { username, password, role: role || 'Participant' };
        if (group) userObj.group = group;
        if (c1) userObj.c1 = c1;
        if (c2) userObj.c2 = c2;
        if (i1) userObj.i1 = i1;
        if (i2) userObj.i2 = i2;
        return userObj;
      }).filter((userObj) => userObj.username && userObj.password);

      if (users.length === 0) {
        alert('No valid users found.');
        return;
      }

      const response = await apiClient.post('/api/admin/users/batch', { users });
      setBatchUserDialog(false);
      setBatchData('');
      loadData();

      let msg = response.data.message;
      if (response.data.errors && response.data.errors.length > 0) {
        msg += '\n\nErrors:\n' + response.data.errors.join('\n');
      }
      alert(msg);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Batch creation failed');
    } finally {
      setActionLoading(false);
    }
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleBackToCharts = async () => {
    try {
      const response = await apiClient.get('/api/auth/next-task');
      const { redirect, patient_id } = response.data;

      if (redirect === 'chart') {
        navigate(`/patient-chart/${patient_id}`);
      } else {
        navigate('/patient-chart/patient_0');
      }
    } catch (err) {
      navigate('/patient-chart/patient_0');
    }
  };

  const handleDownloadTranscript = async (sessionId: string) => {
    try {
      const response = await apiClient.get(`/api/admin/sessions/${sessionId}/details`);
      const s = response.data as SessionDetails;
      
      let content = `MEDICAL-TOM SESSION TRANSCRIPT\n`;
      content += `==============================\n\n`;
      content += `Patient: ${s.patient_name}\n`;
      content += `User: ${s.user.username} (${s.user.email})\n`;
      content += `Role: ${s.user.role || 'N/A'}\n`;
      content += `Started: ${new Date(s.created_at).toLocaleString()}\n`;
      content += `Duration: ${calculateDuration(s.created_at, s.conversation_history)}\n`;
      content += `Model: ${s.model_name}\n\n`;
      content += `CONVERSATION HISTORY\n`;
      content += `--------------------\n\n`;

      s.conversation_history.forEach((msg: any, idx: number) => {
        const role = msg.role === 'user' ? 'Student' : s.patient_name;
        content += `[${idx + 1}] ${role} (${new Date(msg.timestamp).toLocaleTimeString()}):\n`;
        content += `${msg.content}\n`;
        
        if (msg.role === 'assistant' && msg.evaluator_analysis) {
          content += `\n>>> INTERNAL DECISION LOGIC:\n`;
          content += `${msg.evaluator_analysis}\n`;
        }
        content += `\n`;
      });

      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transcript_${s.patient_name.replace(/\s+/g, '_')}_${s.user.username}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download transcript:', err);
      alert('Failed to generate download');
    }
  };

  const sanitizeFilenamePart = (value: string) => {
    const cleaned = value.replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
    return cleaned || 'unknown';
  };

  const triggerDownload = (content: string, filename: string, mimeType = 'text/plain') => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const getSurveyValue = (
    userRow: User,
    key: 'age_group' | 'gender' | 'race' | 'current_occupation' | 'years_of_experience' | 'location_of_practice'
  ): string => {
    if (key === 'current_occupation') {
      const value = userRow.survey_data?.current_occupation || userRow.survey_data?.area_of_expertise;
      return typeof value === 'string' ? value : '';
    }
    const value = userRow.survey_data?.[key];
    return typeof value === 'string' ? value : '';
  };

  const csvEscape = (value: string): string => {
    if (/[",\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const handleDownloadGroupDEJsonl = async () => {
    try {
      const response = await apiClient.get('/api/admin/sessions/export/group-de-traces', {
        responseType: 'blob',
      });
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/x-ndjson' });
      const stamp = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `group_de_sessions_${stamp}.jsonl`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download Group D/E JSONL export:', err);
      alert('Failed to download Group D/E JSONL export');
    }
  };

  const handleDownloadZeroCompleteCredentials = async () => {
    try {
      const response = await apiClient.get('/api/admin/users/export/group-de-zero-complete-credentials', {
        responseType: 'blob',
      });
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'text/plain;charset=utf-8' });
      const stamp = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `group_de_zero_complete_username_password_${stamp}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download 0/3 D/E credentials export:', err);
      alert('Failed to download 0/3 D/E credentials export');
    }
  };

  const handleDownloadUsedZeroCompleteCredentials = async () => {
    try {
      const response = await apiClient.get('/api/admin/users/export/group-de-zero-complete-used-credentials', {
        responseType: 'blob',
      });
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'text/plain;charset=utf-8' });
      const stamp = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `group_de_zero_complete_used_username_password_${stamp}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download used 0/3 D/E credentials export:', err);
      alert('Failed to download used 0/3 D/E credentials export');
    }
  };

  const handleDownloadIncompleteProgress = async (completedCount: 0 | 1 | 2) => {
    try {
      const response = await apiClient.get('/api/admin/users/export/group-de-progress', {
        params: { completed: completedCount },
        responseType: 'blob',
      });
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const stamp = new Date().toISOString().slice(0, 10);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `group_de_completed_${completedCount}_of_3_progress_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error(`Failed to download ${completedCount}/3 D/E progress export:`, err);
      alert(`Failed to download ${completedCount}/3 D/E progress export`);
    }
  };

  const handleDownloadDemographicsCsv = () => {
    const header = [
      'username',
      'group',
      'role',
      'institution',
      'has_completed_survey',
      'age_group',
      'gender',
      'race',
      'current_occupation',
      'years_of_experience',
      'location_of_practice',
    ];

    const rows = users.map((u) => [
      u.username || '',
      u.study_group || '',
      u.role || '',
      u.institution || '',
      u.has_completed_survey ? 'true' : 'false',
      getSurveyValue(u, 'age_group'),
      getSurveyValue(u, 'gender'),
      getSurveyValue(u, 'race'),
      getSurveyValue(u, 'current_occupation'),
      getSurveyValue(u, 'years_of_experience'),
      getSurveyValue(u, 'location_of_practice'),
    ]);

    const csv = [header, ...rows]
      .map((row) => row.map((cell) => csvEscape(cell)).join(','))
      .join('\n');

    const stamp = new Date().toISOString().slice(0, 10);
    triggerDownload(csv, `demographics_${stamp}.csv`, 'text/csv;charset=utf-8');
  };

  const handleDownloadSingleDemographicsJson = (u: User) => {
    const payload = {
      username: u.username,
      group: u.study_group || '',
      role: u.role || '',
      institution: u.institution || '',
      has_completed_survey: Boolean(u.has_completed_survey),
      age_group: getSurveyValue(u, 'age_group'),
      gender: getSurveyValue(u, 'gender'),
      race: getSurveyValue(u, 'race'),
      current_occupation: getSurveyValue(u, 'current_occupation'),
      years_of_experience: getSurveyValue(u, 'years_of_experience'),
      location_of_practice: getSurveyValue(u, 'location_of_practice'),
      survey_data_raw: u.survey_data || {},
    };

    triggerDownload(
      JSON.stringify(payload, null, 2),
      `demographics_${sanitizeFilenamePart(u.username)}.json`,
      'application/json'
    );
  };

  const handleDownloadInternalEvaluation = async (sessionId: string) => {
    try {
      const response = await apiClient.get(`/api/admin/sessions/${sessionId}/details`);
      const s = response.data as SessionDetails;
      const evaluationRows = s.conversation_history
        .map((msg, idx) => {
          if (msg.role !== 'assistant' || !msg.evaluator_analysis) {
            return null;
          }
          return {
            session_id: s.session_id,
            patient_id: s.patient_id,
            patient_name: s.patient_name,
            user: s.user.username,
            turn_index: idx + 1,
            timestamp: msg.timestamp,
            patient_response: msg.content,
            evaluator_analysis: msg.evaluator_analysis,
          };
        })
        .filter((row): row is NonNullable<typeof row> => row !== null);

      if (evaluationRows.length === 0) {
        alert('No internal evaluation data found for this session.');
        return;
      }

      const jsonl = evaluationRows.map((row) => JSON.stringify(row)).join('\n');
      const patientPart = sanitizeFilenamePart(s.patient_name);
      const userPart = sanitizeFilenamePart(s.user.username);
      triggerDownload(
        jsonl,
        `internal_eval_${patientPart}_${userPart}_${s.session_id}.jsonl`,
        'application/x-ndjson'
      );
    } catch (err) {
      console.error('Failed to download internal evaluation:', err);
      alert('Failed to download internal evaluation');
    }
  };

  const handleDownloadSingleEvaluation = (
    msg: SessionDetails['conversation_history'][number],
    idx: number
  ) => {
    if (!selectedSession || msg.role !== 'assistant' || !msg.evaluator_analysis) {
      return;
    }

    const patientPart = sanitizeFilenamePart(selectedSession.patient_name);
    const userPart = sanitizeFilenamePart(selectedSession.user.username);
    const base = `internal_eval_${patientPart}_${userPart}_turn_${idx + 1}`;

    try {
      const parsed = JSON.parse(msg.evaluator_analysis);
      const pretty = JSON.stringify(parsed, null, 2);
      triggerDownload(pretty, `${base}.json`, 'application/json');
    } catch {
      triggerDownload(msg.evaluator_analysis, `${base}.txt`, 'text/plain');
    }
  };

  const handleToggleApproved = async (targetUser: User) => {
    try {
      await apiClient.patch(`/api/admin/users/${targetUser.id}`, {
        approved: !Boolean(targetUser.approved),
      });
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update approval status');
    }
  };

  const handleResetReusableField = async (targetUser: User, field: ResettableField) => {
    const labelMap: Record<ResettableField, string> = {
      survey: 'survey',
      confirmation1: 'C1',
      confirmation2: 'C2',
      intervention1: 'I1',
      intervention2: 'I2',
    };
    const label = labelMap[field];
    const confirmed = window.confirm(`Reset ${label} for ${targetUser.username}?`);
    if (!confirmed) return;

    try {
      await apiClient.post(`/api/admin/users/${targetUser.id}/reset-field`, { field });
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || `Failed to reset ${label}`);
    }
  };

  const hasAssignmentValue = (value?: number | string): boolean => value !== null && value !== undefined && value !== '';

  const normalizeAssignedPatientId = (value?: number | string): string | null => {
    if (value === null || value === undefined || value === '') return null;
    const raw = String(value).trim();
    if (!raw) return null;
    return raw.startsWith('patient_') ? raw : `patient_${raw}`;
  };
  type AssignedTaskSlot = {
    key: string;
    label: string;
    task: 'confirmation' | 'intervention';
    patientId: string;
  };

  const getAssignedTaskSlots = (userRow: User): AssignedTaskSlot[] => {
    const group = (userRow.study_group || '').toUpperCase();
    if (group === 'D') {
      const slots: AssignedTaskSlot[] = [
        { key: 'confirmation1', label: 'C1', task: 'confirmation', patientId: normalizeAssignedPatientId(userRow.confirmation1) || '' },
        { key: 'confirmation2', label: 'C2', task: 'confirmation', patientId: normalizeAssignedPatientId(userRow.confirmation2) || '' },
        { key: 'intervention1', label: 'I1', task: 'intervention', patientId: normalizeAssignedPatientId(userRow.intervention1) || '' },
      ];
      return slots.filter((slot) => Boolean(slot.patientId));
    }
    if (group === 'E') {
      const slots: AssignedTaskSlot[] = [
        { key: 'confirmation1', label: 'C1', task: 'confirmation', patientId: normalizeAssignedPatientId(userRow.confirmation1) || '' },
        { key: 'intervention1', label: 'I1', task: 'intervention', patientId: normalizeAssignedPatientId(userRow.intervention1) || '' },
        { key: 'intervention2', label: 'I2', task: 'intervention', patientId: normalizeAssignedPatientId(userRow.intervention2) || '' },
      ];
      return slots.filter((slot) => Boolean(slot.patientId));
    }
    return [];
  };

  const isCompletedSessionForSlot = (session: Session, task: 'confirmation' | 'intervention', patientId: string, userId: number): boolean => {
    if (session.user_id !== userId || session.patient_id !== patientId) return false;
    if (task === 'confirmation') {
      return !session.is_intervention && Boolean(session.is_finished || session.has_findings);
    }
    return session.is_intervention && Boolean(session.is_finished);
  };

  const studyProgressUsers = users
    .filter((u) => {
      const username = (u.username || '').trim().toLowerCase();
      return username.startsWith('d') || username.startsWith('e');
    })
    .map((u) => {
      const slots = getAssignedTaskSlots(u).map((slot) => ({
        ...slot,
        completed: sessions.some((session) => isCompletedSessionForSlot(session, slot.task, slot.patientId, u.id)),
      }));
      const completedCount = slots.filter((slot) => slot.completed).length;
      return {
        ...u,
        slots,
        assignedCount: slots.length,
        completedCount,
      };
    })
    .sort((a, b) => a.username.localeCompare(b.username));

  const completionSummary = [0, 1, 2, 3].map((count) => ({
    count,
    users: studyProgressUsers.filter((u) => u.completedCount === count).length,
  }));

  const studyProgressUserIds = new Set(studyProgressUsers.map((u) => u.id));
  const studyProgressSessions = sessions.filter((session) => studyProgressUserIds.has(session.user_id));
  const studyProgressConfirmationSessions = studyProgressSessions.filter(
    (session) => !session.is_intervention && Boolean(session.is_finished || session.has_findings)
  );
  const studyProgressInterventionSessions = studyProgressSessions.filter(
    (session) => session.is_intervention && Boolean(session.is_finished)
  );

  const averageMessageCount = (rows: Session[]): number | null => {
    if (rows.length === 0) return null;
    const total = rows.reduce((sum, row) => sum + (row.message_count || 0), 0);
    return total / rows.length;
  };

  const averageConfirmationMessages = averageMessageCount(studyProgressConfirmationSessions);
  const averageInterventionMessages = averageMessageCount(studyProgressInterventionSessions);
  const endedSuccessCount = studyProgressInterventionSessions.filter((session) => session.intervention_success === true).length;
  const endedNotSuccessCount = studyProgressInterventionSessions.filter((session) => session.intervention_success === false).length;
  const endedSuccessDenominator = endedSuccessCount + endedNotSuccessCount;
  const interventionSuccessRate = endedSuccessDenominator > 0
    ? endedSuccessCount / endedSuccessDenominator
    : null;

  const formatAverageMetric = (value: number | null): string => {
    if (value === null) return '-';
    return value.toFixed(1);
  };

  const formatPercentMetric = (value: number | null): string => {
    if (value === null) return '-';
    return `${(value * 100).toFixed(1)}%`;
  };

  const calculateDuration = (start: string, history: any[]) => {
    if (!history || history.length === 0) return '0m 0s';
    const startTime = new Date(start).getTime();
    const lastMsg = history[history.length - 1];
    const endTime = new Date(lastMsg.timestamp).getTime();
    const diffMs = endTime - startTime;
    if (diffMs <= 0) return '0m 0s';
    
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  const confirmationSessions = sessions.filter((s) => !s.is_intervention);
  const interventionSessions = sessions.filter((s) => s.is_intervention);
  const filteredSessions = sessions.filter((s) => {
    if (sessionView === 'confirmation') return !s.is_intervention;
    if (sessionView === 'intervention') return s.is_intervention;
    return true;
  });

  const getSessionStatus = (session: Session) => {
    if (!session.is_intervention) {
      if (session.is_finished || session.has_findings) {
        return { label: 'Submitted', className: 'bg-blue-100 text-blue-800 border-blue-200' };
      }
      return { label: 'In Progress', className: 'bg-gray-100 text-gray-700 border-gray-200' };
    }

    if (!session.is_finished) {
      return { label: 'In Progress', className: 'bg-gray-100 text-gray-700 border-gray-200' };
    }
    if (session.intervention_success === true) {
      return { label: 'Ended - Success', className: 'bg-green-100 text-green-800 border-green-200' };
    }
    if (session.intervention_success === false) {
      return { label: 'Ended - Not Success', className: 'bg-red-100 text-red-800 border-red-200' };
    }
    return { label: 'Ended - Unknown', className: 'bg-amber-100 text-amber-800 border-amber-200' };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-primary text-primary-foreground shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToCharts}
              className="text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-bold">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm">{user?.username}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-primary-foreground hover:bg-primary-foreground/10"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs value={currentTab} onValueChange={setCurrentTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="demographics" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Demographics
            </TabsTrigger>
            <TabsTrigger value="study-progress" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              D/E Progress
            </TabsTrigger>
            <TabsTrigger value="sessions" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Sessions
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {stats && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{stats.total_users}</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {stats.active_users} active
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Total Sessions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{stats.total_sessions}</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Patients Used</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{stats.patient_stats.length}</div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Sessions by Patient</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Patient Name</TableHead>
                          <TableHead className="text-right">Session Count</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {stats.patient_stats.map((stat) => (
                          <TableRow key={stat.patient_id}>
                            <TableCell>{stat.patient_name}</TableCell>
                            <TableCell className="text-right">{stat.session_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="flex justify-end gap-2 mb-4">
              <Button onClick={() => setAddUserDialog(true)}>
                <Users className="h-4 w-4 mr-2" />
                Add User
              </Button>
              <Button variant="outline" onClick={() => setBatchUserDialog(true)}>
                <FileText className="h-4 w-4 mr-2" />
                Batch Import
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Username</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Institution</TableHead>
                      <TableHead>Group</TableHead>
                      <TableHead>Conf 1</TableHead>
                      <TableHead>Conf 2</TableHead>
                      <TableHead>Int 1</TableHead>
                      <TableHead>Int 2</TableHead>
                      <TableHead>Approved</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {u.username}
                            {u.is_admin && (
                              <Badge variant="default">Admin</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{u.email || '-'}</TableCell>
                        <TableCell>{u.role || '-'}</TableCell>
                        <TableCell>{u.institution || '-'}</TableCell>
                        <TableCell>
                            {u.study_group ? <Badge variant="outline">{u.study_group}</Badge> : '-'}
                        </TableCell>
                        <TableCell>{u.confirmation1 !== undefined ? u.confirmation1 : '-'}</TableCell>
                        <TableCell>{u.confirmation2 !== undefined ? u.confirmation2 : '-'}</TableCell>
                        <TableCell>{u.intervention1 !== undefined ? u.intervention1 : '-'}</TableCell>
                        <TableCell>{u.intervention2 !== undefined ? u.intervention2 : '-'}</TableCell>
                        <TableCell>
                          {u.approved ? (
                            <Badge className="bg-green-500">Yes</Badge>
                          ) : (
                            <Badge variant="outline">No</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {u.is_active ? (
                            <Badge className="bg-green-500">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <XCircle className="h-3 w-3 mr-1" />
                              Inactive
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{new Date(u.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingUser({ ...u, password: '' }); // Don't preload password
                                setEditUserDialog(true);
                              }}
                              disabled={u.is_admin}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleUserActive(u.id)}
                              disabled={u.is_admin}
                            >
                              {u.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => openDeleteDialog('user', u.id)}
                              disabled={u.is_admin || u.id === user?.id}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* D/E Progress Tab */}
          <TabsContent value="study-progress" className="space-y-6">
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => handleDownloadIncompleteProgress(0)}>
                <Download className="h-4 w-4 mr-2" />
                Download 0/3 Slot Data
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleDownloadIncompleteProgress(1)}>
                <Download className="h-4 w-4 mr-2" />
                Download 1/3 Slot Data
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleDownloadIncompleteProgress(2)}>
                <Download className="h-4 w-4 mr-2" />
                Download 2/3 Slot Data
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadZeroCompleteCredentials}>
                <Download className="h-4 w-4 mr-2" />
                Download 0/3 Credentials
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadUsedZeroCompleteCredentials}>
                <Download className="h-4 w-4 mr-2" />
                Download Used 0/3 Credentials
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {completionSummary.map((item) => (
                <Card key={`completion-${item.count}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Completed {item.count}/3
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{item.users}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Avg Messages: Confirmation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{formatAverageMetric(averageConfirmationMessages)}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Submitted D/E confirmation sessions
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Avg Messages: Intervention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{formatAverageMetric(averageInterventionMessages)}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Ended D/E intervention sessions
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Intervention Success Rate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{formatPercentMetric(interventionSuccessRate)}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {endedSuccessCount}/{endedSuccessDenominator || 0} ended interventions
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Group D/E Completion and Approval</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Username</TableHead>
                      <TableHead>Group</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Assigned Slots</TableHead>
                      <TableHead>Approved</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {studyProgressUsers.map((u) => (
                      <TableRow key={`study-progress-${u.id}`}>
                        <TableCell>{u.username}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{u.study_group || '-'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{u.completedCount}/{u.assignedCount}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            {u.slots.map((slot) => (
                              <Badge
                                key={`${u.id}-${slot.key}`}
                                variant="outline"
                                className={slot.completed ? 'border-green-200 text-green-700' : 'border-gray-200 text-gray-600'}
                              >
                                {slot.label}: {slot.patientId.replace('patient_', '')} {slot.completed ? 'done' : 'pending'}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {u.approved ? (
                            <Badge className="bg-green-500">Yes</Badge>
                          ) : (
                            <Badge variant="outline">No</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap justify-end gap-1">
                            <Button
                              variant={u.approved ? 'outline' : 'default'}
                              size="sm"
                              onClick={() => handleToggleApproved(u)}
                            >
                              {u.approved ? 'Set No' : 'Set Yes'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResetReusableField(u, 'survey')}
                            >
                              Reset Survey
                            </Button>
                            {hasAssignmentValue(u.confirmation1) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResetReusableField(u, 'confirmation1')}
                              >
                                Reset C1
                              </Button>
                            )}
                            {hasAssignmentValue(u.confirmation2) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResetReusableField(u, 'confirmation2')}
                              >
                                Reset C2
                              </Button>
                            )}
                            {hasAssignmentValue(u.intervention1) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResetReusableField(u, 'intervention1')}
                              >
                                Reset I1
                              </Button>
                            )}
                            {hasAssignmentValue(u.intervention2) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResetReusableField(u, 'intervention2')}
                              >
                                Reset I2
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {studyProgressUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                          No Group D/E users found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Demographics Tab */}
          <TabsContent value="demographics">
            <div className="flex justify-end gap-2 mb-4">
              <Button variant="outline" onClick={handleDownloadDemographicsCsv}>
                <Download className="h-4 w-4 mr-2" />
                Download CSV
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Username</TableHead>
                      <TableHead>Group</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Institution</TableHead>
                      <TableHead>Survey Done</TableHead>
                      <TableHead>Age Group</TableHead>
                      <TableHead>Gender</TableHead>
                      <TableHead>Race</TableHead>
                      <TableHead>Current Occupation</TableHead>
                      <TableHead>Years Experience</TableHead>
                      <TableHead>Practice Location</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={`demo-${u.id}`}>
                        <TableCell>{u.username}</TableCell>
                        <TableCell>{u.study_group || '-'}</TableCell>
                        <TableCell>{u.role || '-'}</TableCell>
                        <TableCell>{u.institution || '-'}</TableCell>
                        <TableCell>
                          {u.has_completed_survey ? (
                            <Badge className="bg-green-500">Yes</Badge>
                          ) : (
                            <Badge variant="outline">No</Badge>
                          )}
                        </TableCell>
                        <TableCell>{getSurveyValue(u, 'age_group') || '-'}</TableCell>
                        <TableCell>{getSurveyValue(u, 'gender') || '-'}</TableCell>
                        <TableCell>{getSurveyValue(u, 'race') || '-'}</TableCell>
                        <TableCell>{getSurveyValue(u, 'current_occupation') || '-'}</TableCell>
                        <TableCell>{getSurveyValue(u, 'years_of_experience') || '-'}</TableCell>
                        <TableCell>{getSurveyValue(u, 'location_of_practice') || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadSingleDemographicsJson(u)}
                          >
                            <Download className="h-3 w-3 mr-1" />
                            JSON
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant={sessionView === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSessionView('all')}
                  >
                    All ({sessions.length})
                  </Button>
                  <Button
                    variant={sessionView === 'confirmation' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSessionView('confirmation')}
                  >
                    Confirmation ({confirmationSessions.length})
                  </Button>
                  <Button
                    variant={sessionView === 'intervention' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSessionView('intervention')}
                  >
                    Intervention ({interventionSessions.length})
                  </Button>
                </div>
                <Button variant="outline" size="sm" onClick={handleDownloadGroupDEJsonl}>
                  <Download className="h-4 w-4 mr-2" />
                  Download D/E JSONL
                </Button>
              </div>
              <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Patient</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Messages</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Updated</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSessions.map((s) => (
                      <TableRow key={s.session_id}>
                        <TableCell>{s.patient_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={s.is_intervention ? 'border-purple-200 text-purple-700' : 'border-blue-200 text-blue-700'}>
                            {s.is_intervention ? 'Intervention' : 'Confirmation'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getSessionStatus(s).className}>
                            {getSessionStatus(s).label}
                          </Badge>
                        </TableCell>
                        <TableCell>{s.username || `User #${s.user_id}`}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{s.message_count}</Badge>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {s.model_name}
                          </code>
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(s.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(s.updated_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 justify-end">
                            {!s.is_intervention && (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => navigate(`/evaluation/${s.session_id}`)}
                              >
                                <BarChart3 className="h-3 w-3 mr-1" />
                                Evaluation
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/session/${s.session_id}`)}
                            >
                              <MessageSquare className="h-3 w-3 mr-1" />
                              Transcript
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/patient-feedback-view/${s.session_id}`)}
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              User Feedback
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadTranscript(s.session_id)}
                              title="Download Transcript"
                            >
                              <Download className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewSession(s.session_id)}
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => openDeleteDialog('session', s.session_id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* View Session Dialog */}
        <Dialog open={viewSessionDialog} onOpenChange={setViewSessionDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Session Details</DialogTitle>
            </DialogHeader>
            {selectedSession && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold">Patient:</span> {selectedSession.patient_name}
                  </div>
                  <div>
                    <span className="font-semibold">User:</span> {selectedSession.user.username} ({selectedSession.user.email})
                  </div>
                  <div>
                    <span className="font-semibold">Role:</span> {selectedSession.user.role || 'N/A'}
                  </div>
                  <div>
                    <span className="font-semibold">Institution:</span> {selectedSession.user.institution || 'N/A'}
                  </div>
                  <div>
                    <span className="font-semibold">Type:</span> {selectedSession.is_intervention ? 'Intervention' : 'Confirmation'}
                  </div>
                  <div>
                    <span className="font-semibold">Status:</span>{' '}
                    {selectedSession.is_intervention
                      ? (selectedSession.is_finished
                        ? (selectedSession.intervention_success === true
                          ? 'Ended - Success'
                          : (selectedSession.intervention_success === false ? 'Ended - Not Success' : 'Ended - Unknown'))
                        : 'In Progress')
                      : ((selectedSession.is_finished || selectedSession.hidden_concerns) ? 'Submitted' : 'In Progress')}
                  </div>
                  <div>
                    <span className="font-semibold text-blue-600">Started:</span> {new Date(selectedSession.created_at).toLocaleString()}
                  </div>
                  <div>
                    <span className="font-semibold text-orange-600">Total Time Spent:</span> {calculateDuration(selectedSession.created_at, selectedSession.conversation_history)}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">Conversation History</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadInternalEvaluation(selectedSession.session_id)}
                      title="Download Internal Evaluation (JSONL)"
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Internal Eval
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {selectedSession.conversation_history.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded ${
                          msg.role === 'user' ? 'bg-blue-100' : 'bg-gray-100'
                        }`}
                      >
                        <p className="text-xs font-semibold mb-1">
                          {msg.role === 'user' ? 'Student' : selectedSession.patient_name}
                        </p>
                        <p className="text-sm">{msg.content}</p>
                        
                        {/* Evaluator Analysis (Admin Only metadata) */}
                        {msg.role === 'assistant' && msg.evaluator_analysis && (
                          <div className="mt-2 p-2 bg-orange-50 border border-orange-100 rounded text-[10px] text-orange-800 italic">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1 font-bold uppercase tracking-tighter opacity-70">
                                <Target className="h-3 w-3" />
                                Internal Decision Logic
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 px-1 text-orange-700 hover:text-orange-900 hover:bg-orange-100"
                                onClick={() => handleDownloadSingleEvaluation(msg, idx)}
                                title="Download this internal evaluation"
                              >
                                <Download className="h-3 w-3" />
                              </Button>
                            </div>
                            <div className="whitespace-pre-wrap">
                              {msg.evaluator_analysis}
                            </div>
                          </div>
                        )}

                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(msg.timestamp).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setViewSessionDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmDialog} onOpenChange={setDeleteConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this {deleteTarget?.type}?
                {deleteTarget?.type === 'user' && ' All their sessions will also be deleted.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteConfirm}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add User Dialog */}
        <Dialog open={addUserDialog} onOpenChange={setAddUserDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
              <DialogDescription>Create a single user account.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddUser}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="new-username">Username</Label>
                  <Input 
                    id="new-username" 
                    value={newUser.username}
                    onChange={e => setNewUser({...newUser, username: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">Password</Label>
                  <Input 
                    id="new-password" 
                    type="password"
                    value={newUser.password}
                    onChange={e => setNewUser({...newUser, password: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-role">Role</Label>
                  <Input 
                    id="new-role" 
                    placeholder="e.g., Medical Student"
                    value={newUser.role}
                    onChange={e => setNewUser({...newUser, role: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-institution">Institution</Label>
                  <Input 
                    id="new-institution" 
                    placeholder="e.g., University"
                    value={newUser.institution}
                    onChange={e => setNewUser({...newUser, institution: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-group">Group (A/B/C/D/E)</Label>
                  <Input 
                    id="new-group" 
                    placeholder="A, B, C, D, or E"
                    value={newUser.study_group}
                    onChange={e => setNewUser({...newUser, study_group: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-c1">Conf 1</Label>
                    <Input 
                      id="new-c1" 
                      placeholder="ID (e.g. 0)"
                      value={newUser.confirmation1}
                      onChange={e => setNewUser({...newUser, confirmation1: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-c2">Conf 2</Label>
                    <Input 
                      id="new-c2" 
                      placeholder="ID"
                      value={newUser.confirmation2}
                      onChange={e => setNewUser({...newUser, confirmation2: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-i1">Int 1</Label>
                    <Input 
                      id="new-i1" 
                      placeholder="ID"
                      value={newUser.intervention1}
                      onChange={e => setNewUser({...newUser, intervention1: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-i2">Int 2</Label>
                    <Input 
                      id="new-i2" 
                      placeholder="ID"
                      value={newUser.intervention2}
                      onChange={e => setNewUser({...newUser, intervention2: e.target.value})}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" type="button" onClick={() => setAddUserDialog(false)}>Cancel</Button>
                <Button type="submit" disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create User'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editUserDialog} onOpenChange={setEditUserDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>Update user details and progress.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditUser}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-username">Username</Label>
                  <Input id="edit-username" value={editingUser?.username || ''} onChange={e => setEditingUser({...editingUser, username: e.target.value})} required />
                </div>
                {/* Optional: Password reset field could go here, but omitted for simplicity */}
                <div className="space-y-2">
                  <Label htmlFor="edit-role">Role</Label>
                  <Input 
                    id="edit-role" 
                    value={editingUser?.role || ''}
                    onChange={e => setEditingUser({...editingUser, role: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-institution">Institution</Label>
                  <Input 
                    id="edit-institution" 
                    value={editingUser?.institution || ''}
                    onChange={e => setEditingUser({...editingUser, institution: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-approved">Approved</Label>
                  <Button
                    id="edit-approved"
                    type="button"
                    variant={editingUser?.approved ? 'default' : 'outline'}
                    onClick={() => setEditingUser({...editingUser, approved: !Boolean(editingUser?.approved)})}
                  >
                    {editingUser?.approved ? 'Yes' : 'No'}
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-group">Group (A/B/C/D/E)</Label>
                  <Input 
                    id="edit-group" 
                    value={editingUser?.study_group || ''}
                    onChange={e => setEditingUser({...editingUser, study_group: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-c1">Conf 1</Label>
                    <Input 
                      id="edit-c1" 
                      value={editingUser?.confirmation1 !== null && editingUser?.confirmation1 !== undefined ? editingUser.confirmation1 : ''}
                      onChange={e => setEditingUser({...editingUser, confirmation1: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-c2">Conf 2</Label>
                    <Input 
                      id="edit-c2" 
                      value={editingUser?.confirmation2 !== null && editingUser?.confirmation2 !== undefined ? editingUser.confirmation2 : ''}
                      onChange={e => setEditingUser({...editingUser, confirmation2: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-i1">Int 1</Label>
                    <Input 
                      id="edit-i1" 
                      value={editingUser?.intervention1 !== null && editingUser?.intervention1 !== undefined ? editingUser.intervention1 : ''}
                      onChange={e => setEditingUser({...editingUser, intervention1: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-i2">Int 2</Label>
                    <Input 
                      id="edit-i2" 
                      value={editingUser?.intervention2 !== null && editingUser?.intervention2 !== undefined ? editingUser.intervention2 : ''}
                      onChange={e => setEditingUser({...editingUser, intervention2: e.target.value})}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" type="button" onClick={() => setEditUserDialog(false)}>Cancel</Button>
                <Button type="submit" disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Batch User Dialog */}
        <Dialog open={batchUserDialog} onOpenChange={setBatchUserDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Batch Import Users</DialogTitle>
              <DialogDescription>
                Paste the generated CSV or spreadsheet rows directly, with or without the header row:<br/>
                <code>username,password,role,group,confirmation1,confirmation2,intervention1,intervention2,assigned_ids_distinct</code>
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Textarea 
                className="font-mono min-h-[200px]"
                placeholder="username,password,role,group,confirmation1,confirmation2,intervention1,intervention2,assigned_ids_distinct&#10;d001,examplePass,Physician,D,1021,1316,150,,True"
                value={batchData}
                onChange={e => setBatchData(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBatchUserDialog(false)}>Cancel</Button>
              <Button onClick={handleBatchUpload} disabled={actionLoading || !batchData.trim()}>
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Import Users'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;









